import { useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { TIME_SLOTS } from "../data/pricing";

const DAYS = ["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"];
const toMin = (t) => { const [h, m] = t.split(":"); return +h * 60 + +m; };
const iso = (d) => d.toISOString().split("T")[0];

const mondayOf = (d) => {
  const x = new Date(d);
  x.setHours(12, 0, 0, 0);
  x.setDate(x.getDate() - ((x.getDay() + 6) % 7));
  return x;
};

const STATUS_CLS = {
  nuovo: "bg-[#7EC8E3]/25 border-[#7EC8E3]/60 text-[#A0E0FF]",
  confermato: "bg-green-400/20 border-green-400/50 text-green-200",
  completato: "bg-slate-400/20 border-slate-400/40 text-slate-200",
};

export const AdminCalendar = ({ bookings, blockedDays = [], onToggleDay }) => {
  const [monday, setMonday] = useState(() => mondayOf(new Date()));
  const days = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(monday);
    d.setDate(monday.getDate() + i);
    return d;
  });
  const shift = (n) => setMonday((m) => { const d = new Date(m); d.setDate(d.getDate() + n * 7); return d; });
  const active = bookings.filter((b) => b.status !== "annullato");
  const todayIso = iso(new Date());

  const blockedMap = Object.fromEntries(blockedDays.map((b) => [b.date, b.reason || "Bloccato"]));

  const cell = (dateIso, slot) => {
    if (blockedMap[dateIso] !== undefined) return { type: "dayblocked" };
    const dayB = active.filter((b) => b.date === dateIso);
    const own = dayB.find((b) => b.time === slot);
    if (own) return { type: "booking", b: own };
    if (dayB.some((b) => Math.abs(toMin(b.time) - toMin(slot)) < 180)) return { type: "blocked" };
    return { type: "free" };
  };

  const fmt = (d) => d.toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit" });

  return (
    <div className="card-metallic rounded-2xl p-4 sm:p-5" data-testid="admin-calendar">
      <div className="flex items-center justify-between mb-4">
        <button data-testid="calendar-prev-week" onClick={() => shift(-1)} className="btn-outline-blue rounded-full p-2"><ChevronLeft size={16} /></button>
        <div className="text-center">
          <div className="font-bold text-slate-100" data-testid="calendar-week-label">
            {fmt(days[0])} – {fmt(days[6])}
          </div>
          <button data-testid="calendar-today" onClick={() => setMonday(mondayOf(new Date()))} className="text-xs text-[#7EC8E3] hover:underline">Oggi</button>
        </div>
        <button data-testid="calendar-next-week" onClick={() => shift(1)} className="btn-outline-blue rounded-full p-2"><ChevronRight size={16} /></button>
      </div>

      <div className="overflow-x-auto">
        <div className="grid min-w-[720px]" style={{ gridTemplateColumns: "64px repeat(7, 1fr)" }}>
          <div />
          {days.map((d, i) => {
            const di = iso(d);
            const blocked = blockedMap[di] !== undefined;
            return (
              <button type="button" key={di} data-testid={`cal-day-${di}`} onClick={() => onToggleDay && onToggleDay(di, blocked)}
                title={blocked ? "Clicca per sbloccare la giornata" : "Clicca per bloccare la giornata (ferie / non disponibile)"}
                className={`text-center pb-2 text-xs rounded-lg mx-0.5 transition-colors hover:bg-[#7EC8E3]/10 ${blocked ? "bg-red-400/10" : ""} ${di === todayIso ? "text-[#A0E0FF] font-bold" : "text-slate-400"}`}>
                <div>{DAYS[i]}</div>
                <div className="text-sm font-semibold text-slate-200">{fmt(d)}</div>
                {blocked && <div className="text-[10px] text-red-200 truncate px-1">{blockedMap[di]}</div>}
              </button>
            );
          })}
          {TIME_SLOTS.map((slot) => (
            <div key={slot} className="contents">
              <div className="text-xs text-slate-400 pr-2 py-1 flex items-center justify-end">{slot}</div>
              {days.map((d) => {
                const di = iso(d);
                const c = cell(di, slot);
                if (c.type === "dayblocked") {
                  return <div key={di + slot} data-testid={`cal-cell-${di}-${slot}`} className="m-0.5 rounded-lg bg-red-400/10 border border-red-400/20 min-h-[38px]" />;
                }
                if (c.type === "booking") {
                  return (
                    <div key={di + slot} data-testid={`cal-cell-${di}-${slot}`} title={`${c.b.first_name} ${c.b.last_name} · ${c.b.total}€`}
                      className={`m-0.5 rounded-lg border px-1.5 py-1 text-[11px] leading-tight truncate ${STATUS_CLS[c.b.status] || STATUS_CLS.nuovo}`}>
                      <div className="font-semibold truncate">{c.b.first_name} {c.b.last_name}</div>
                      <div className="truncate opacity-80">{c.b.location || c.b.package}</div>
                    </div>
                  );
                }
                if (c.type === "blocked") {
                  return <div key={di + slot} data-testid={`cal-cell-${di}-${slot}`} className="m-0.5 rounded-lg bg-slate-500/10 border border-slate-500/15 min-h-[38px]" style={{ backgroundImage: "repeating-linear-gradient(45deg, transparent 0 6px, rgba(148,163,184,0.12) 6px 8px)" }} />;
                }
                return <div key={di + slot} data-testid={`cal-cell-${di}-${slot}`} className="m-0.5 rounded-lg border border-dashed border-[#7EC8E3]/25 min-h-[38px] flex items-center justify-center text-[10px] text-[#7EC8E3]/60">libero</div>;
              })}
            </div>
          ))}
        </div>
      </div>

      <div className="flex flex-wrap gap-4 mt-4 text-[11px] text-slate-400">
        <span className="flex items-center gap-1.5"><i className="w-3 h-3 rounded border border-dashed border-[#7EC8E3]/40" /> Libero</span>
        <span className="flex items-center gap-1.5"><i className="w-3 h-3 rounded bg-[#7EC8E3]/25 border border-[#7EC8E3]/60" /> Nuovo</span>
        <span className="flex items-center gap-1.5"><i className="w-3 h-3 rounded bg-green-400/20 border border-green-400/50" /> Confermato</span>
        <span className="flex items-center gap-1.5"><i className="w-3 h-3 rounded bg-slate-400/20 border border-slate-400/40" /> Completato</span>
        <span className="flex items-center gap-1.5"><i className="w-3 h-3 rounded bg-slate-500/20" /> Occupato (entro 3h)</span>
        <span className="flex items-center gap-1.5"><i className="w-3 h-3 rounded bg-red-400/10 border border-red-400/30" /> Giornata bloccata</span>
        <span className="ml-auto text-slate-500">Clicca su un giorno per bloccarlo/sbloccarlo</span>
      </div>
    </div>
  );
};
